import { useState, useRef, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Play, Square, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { playGreetingAudio } from '@/hooks/api/useGreetings'
import type { Voice } from '@/types/assistant'

interface VoiceSelectionCardProps {
  selectedVoice?: Voice | null
  onClick: () => void
  greeting?: string
  defaultLanguage?: string
  assistantId?: number
  className?: string
}

export function VoiceSelectionCard({
  selectedVoice,
  onClick,
  greeting = 'Hello, how can I help you today?',
  defaultLanguage = 'en-US',
  assistantId,
  className,
}: VoiceSelectionCardProps) {
  const { t } = useTranslation()
  const [isPlaying, setIsPlaying] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const cleanupAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.removeAttribute('src')
      audioRef.current = null
    }
    setIsPlaying(false)
  }, [])

  useEffect(() => () => cleanupAudio(), [cleanupAudio])

  const handlePlayPreview = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!selectedVoice) return

    if (isPlaying) {
      cleanupAudio()
      return
    }

    try {
      setIsPlaying(true)
      const audio = await playGreetingAudio({
        text: greeting,
        language: defaultLanguage,
        id: assistantId || 0,
        voice_id: selectedVoice.id,
      })
      audioRef.current = audio
      audio.onended = () => {
        audioRef.current = null
        setIsPlaying(false)
      }
    } catch (error) {
      console.error('Failed to play audio preview:', error)
      setIsPlaying(false)
    }
  }

  if (!selectedVoice) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'flex w-full items-center gap-4 rounded-lg border border-gray-200 bg-white p-4 text-left transition-colors',
          'hover:border-gray-300 hover:bg-gray-50',
          'dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600 dark:hover:bg-gray-700',
          className
        )}
      >
        <div
          className={cn(
            'flex h-12 w-12 shrink-0 items-center justify-center rounded-full',
            'bg-gray-100 text-gray-400 dark:bg-gray-700'
          )}
        >
          <Play className="h-5 w-5" />
        </div>

        <div className="flex-1">
          <p className="font-medium text-gray-500 dark:text-gray-400">
            {t('resources.assistants.voice_drawer.select_a_voice')}
          </p>
          <p className="text-sm text-gray-400 dark:text-gray-500">
            {t('resources.assistants.voice_drawer.subtitle')}
          </p>
        </div>

        <ChevronRight className="h-5 w-5 text-gray-400" />
      </button>
    )
  }

  const handleCardClick = () => {
    onClick()
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      className={cn(
        'flex w-full cursor-pointer items-center gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4 text-left transition-colors',
        'hover:border-gray-300 hover:bg-gray-100',
        'dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600 dark:hover:bg-gray-700',
        'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900',
        className
      )}
    >
      {/* Play button */}
      <button
        type="button"
        onClick={handlePlayPreview}
        aria-label={isPlaying ? t('resources.assistants.voice_drawer.playing_preview') : t('resources.assistants.voice_drawer.play_preview')}
        className={cn(
          'flex h-12 w-12 shrink-0 items-center justify-center rounded-full transition-colors',
          'border-2 border-gray-300 bg-white text-gray-600',
          'hover:border-primary-500 hover:text-primary-600',
          'dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300',
          isPlaying && 'border-primary-500 text-primary-600'
        )}
      >
        {isPlaying ? <Square className="h-4 w-4 fill-current" /> : <Play className="h-5 w-5 ml-0.5" />}
      </button>

      {/* Voice info */}
      <div className="flex flex-1 flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            {selectedVoice.display_name}
          </span>
          {selectedVoice.is_default && (
            <span className="text-sm text-gray-500">({t('resources.assistants.voice_drawer.default')})</span>
          )}
        </div>
        {selectedVoice.description && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {selectedVoice.description}
          </p>
        )}
      </div>

      <ChevronRight className="h-5 w-5 text-gray-400" />
    </div>
  )
}

export default VoiceSelectionCard
